import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  MapPin, Plus, Loader2, Star, Pencil, Trash2, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';

/**
 * SavedBranchesCard — Manager Profile section for CRUD on saved branch
 * locations. One branch can be marked default (per manager). When a manager
 * posts a shift, PostShift.jsx auto-fills from the default (or any picked)
 * branch. See backend endpoints under `/api/manager/branches`.
 */
export default function SavedBranchesCard() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null | branch object
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/manager/branches');
      setBranches(res.data?.branches || []);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load saved branches'));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (b) => { setEditing(b); setModalOpen(true); };

  const handleSave = async (form) => {
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/manager/branches/${editing.id}`, form);
        toast.success('Branch updated');
      } else {
        await api.post('/manager/branches', form);
        toast.success('Branch saved');
      }
      setModalOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save branch'));
    } finally {
      setBusy(false);
    }
  };

  const handleSetDefault = async (id) => {
    setBusy(true);
    try {
      await api.post(`/manager/branches/${id}/set-default`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to set default'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Delete saved branch "${b.label}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/manager/branches/${b.id}`);
      toast.success('Branch deleted');
      await load();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to delete'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Card className="border-slate-200" data-testid="saved-branches-card">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-[#0f1b3d]/10 flex items-center justify-center">
                <MapPin className="h-5 w-5 text-[#0f1b3d]" />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-900">Saved branches</h2>
                <p className="text-[12px] text-slate-500 leading-snug">
                  Save your regular branches once — auto-fill when posting shifts.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              className="rounded-full h-9 px-3 bg-[#0f1b3d] hover:bg-[#1a2d5a]"
              onClick={openNew}
              disabled={busy}
              data-testid="saved-branch-add-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 py-6 text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              <p className="text-[12.5px]">Loading branches…</p>
            </div>
          ) : branches.length === 0 ? (
            <div className="mt-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-5 text-center" data-testid="saved-branches-empty">
              <MapPin className="h-6 w-6 text-slate-300 mx-auto mb-2" />
              <p className="text-[13px] text-slate-600 font-medium">No saved branches yet</p>
              <p className="text-[11.5px] text-slate-500 mt-1">
                Save your branch details once so you don&apos;t have to re-enter them each time.
              </p>
            </div>
          ) : (
            <ul className="mt-4 space-y-2.5" data-testid="saved-branches-list">
              {branches.map((b) => (
                <li
                  key={b.id}
                  className={`rounded-lg border p-3.5 transition-colors ${
                    b.isDefault ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`saved-branch-row-${b.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[13.5px] font-semibold text-slate-900">{b.label}</p>
                        {b.isDefault && (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] font-bold uppercase tracking-wider">
                            <Star className="h-3 w-3 mr-0.5 fill-current" /> Default
                          </Badge>
                        )}
                      </div>
                      <p className="text-[12px] text-slate-600 mt-0.5">{b.branchName}</p>
                      <p className="text-[11.5px] text-slate-500 mt-0.5">
                        {[b.location, b.postcode, b.city, b.area].filter(Boolean).join(' · ') || '—'}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      {!b.isDefault && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 rounded-full text-[11.5px] px-2.5"
                          onClick={() => handleSetDefault(b.id)}
                          disabled={busy}
                          data-testid={`saved-branch-set-default-${b.id}`}
                        >
                          <Star className="h-3 w-3 mr-1" /> Default
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0 rounded-full"
                        onClick={() => openEdit(b)}
                        disabled={busy}
                        data-testid={`saved-branch-edit-${b.id}`}
                        aria-label="Edit branch"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0 rounded-full text-rose-600 border-rose-200 hover:bg-rose-50"
                        onClick={() => handleDelete(b)}
                        disabled={busy}
                        data-testid={`saved-branch-delete-${b.id}`}
                        aria-label="Delete branch"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <SavedBranchDialog
        open={modalOpen}
        onOpenChange={setModalOpen}
        editing={editing}
        onSave={handleSave}
        busy={busy}
      />
    </>
  );
}

function SavedBranchDialog({ open, onOpenChange, editing, onSave, busy }) {
  const [form, setForm] = useState({
    label: '', branchName: '', location: '', postcode: '', city: '', area: '', isDefault: false,
  });
  useEffect(() => {
    if (open) {
      setForm({
        label: editing?.label || '',
        branchName: editing?.branchName || '',
        location: editing?.location || '',
        postcode: editing?.postcode || '',
        city: editing?.city || '',
        area: editing?.area || '',
        isDefault: editing?.isDefault || false,
      });
    }
  }, [open, editing]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.label.trim()) { toast.error('Please add a short label'); return; }
    if (!form.branchName.trim()) { toast.error('Branch name is required'); return; }
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="saved-branch-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit branch' : 'Save a branch'}</DialogTitle>
          <DialogDescription>
            Store your branch details once so you can auto-fill them when posting future shifts.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="sb-label" className="text-slate-700 text-[12.5px]">Short label *</Label>
            <Input
              id="sb-label"
              placeholder="e.g. Croydon PO"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              className="h-11 rounded-xl"
              data-testid="saved-branch-input-label"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sb-branch" className="text-slate-700 text-[12.5px]">Branch name *</Label>
            <Input
              id="sb-branch"
              placeholder="e.g. Royal Mail Post Office"
              value={form.branchName}
              onChange={(e) => setForm({ ...form, branchName: e.target.value })}
              className="h-11 rounded-xl"
              data-testid="saved-branch-input-branchName"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sb-location" className="text-slate-700 text-[12.5px]">Location / address</Label>
            <Input
              id="sb-location"
              placeholder="123 High St"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="h-11 rounded-xl"
              data-testid="saved-branch-input-location"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="sb-postcode" className="text-slate-700 text-[12.5px]">Postcode</Label>
              <Input
                id="sb-postcode"
                placeholder="CR0 1AA"
                value={form.postcode}
                onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                className="h-11 rounded-xl uppercase"
                data-testid="saved-branch-input-postcode"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sb-city" className="text-slate-700 text-[12.5px]">City</Label>
              <Input
                id="sb-city"
                placeholder="Croydon"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="h-11 rounded-xl"
                data-testid="saved-branch-input-city"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sb-area" className="text-slate-700 text-[12.5px]">Area / region</Label>
            <Input
              id="sb-area"
              placeholder="South London"
              value={form.area}
              onChange={(e) => setForm({ ...form, area: e.target.value })}
              className="h-11 rounded-xl"
              data-testid="saved-branch-input-area"
            />
          </div>
          <label className="flex items-center gap-2 text-[12.5px] text-slate-700 pt-1">
            <input
              type="checkbox"
              checked={form.isDefault}
              onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300"
              data-testid="saved-branch-input-default"
            />
            <Star className="h-3.5 w-3.5 text-amber-500" />
            <span>Use as my default branch</span>
          </label>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-full h-11"
              onClick={() => onOpenChange(false)}
              data-testid="saved-branch-dialog-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="rounded-full h-11 bg-[#0f1b3d] hover:bg-[#1a2d5a] text-white"
              disabled={busy}
              data-testid="saved-branch-dialog-save"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <><CheckCircle2 className="h-4 w-4 mr-2" /> {editing ? 'Save changes' : 'Save branch'}</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
